// Dev-time guards for heavy handlers/long tasks.
// Safe to import from AppShell/main; no-ops in prod.

let installed = false;

export function installPerfGuards() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  // Only run in development or when explicitly enabled
  const meta = import.meta as ImportMeta;
  const appWindow = window as Window & { __APP_ENV__?: string; __PROFILE_EVENTS__?: string };
  const env =
    meta.env?.MODE ||
    appWindow.__APP_ENV__ ||
    process.env.NODE_ENV;
  const enabled =
    env !== 'production' ||
    appWindow.__PROFILE_EVENTS__ === '1';

  if (!enabled) return;

  try {
    // Long task observer — throttle to avoid log floods; only log duration (no heavy formatting)
    if ('PerformanceObserver' in window) {
      let longTaskCount = 0;
      const po = new PerformanceObserver((list) => {
        for (const e of list.getEntries()) {
          const duration = Math.round(e.duration);
          longTaskCount += 1;
          const shouldLog = duration >= 50 || longTaskCount % 5 === 0;
          if (shouldLog) {
            // eslint-disable-next-line no-console
            console.warn('[perf] long task', { name: e.name, duration, startTime: Math.round(e.startTime) });
          }
        }
      });
      po.observe({ type: 'longtask', buffered: true });
    }
  } catch {
    /* noop */
  }

  // Wrap message handlers to surface heavy work
  const _add = window.addEventListener.bind(window);
  const patchedAddEventListener = function patchedAddEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions
  ) {
    if (type === 'message' && typeof listener === 'function') {
      const wrapped: EventListener = function (this: Window, ev: Event) {
        const t0 = performance.now();
        try {
          return listener.call(this, ev);
        } finally {
          const dt = performance.now() - t0;
          if (dt > 48) {
            const msgEvent = ev as MessageEvent;
            // eslint-disable-next-line no-console
            console.warn('[perf] message handler slow', Math.round(dt), 'ms', {
              origin: msgEvent.origin || 'unknown',
              dataType: typeof msgEvent.data,
            });
          }
        }
      };
      // Keep identity to allow removeEventListener on the original ref
      try { (wrapped as EventListener & { __orig?: EventListenerOrEventListenerObject }).__orig = listener; } catch {}
      return _add(type, wrapped, options);
    }
    return _add(type, listener, options);
  } as Window['addEventListener'];
  window.addEventListener = patchedAddEventListener;

  const _remove = window.removeEventListener.bind(window);
  const patchedRemoveEventListener = function patchedRemoveEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | EventListenerOptions
  ) {
    const ref = (listener as EventListener & { __orig?: EventListenerOrEventListenerObject }).__orig || listener;
    return _remove(type, ref, options);
  } as Window['removeEventListener'];
  window.removeEventListener = patchedRemoveEventListener;
}
